/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import Highcharts, { SeriesMapDataOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsMap from 'highcharts/modules/map';
import mapData from '@highcharts/map-collection/custom/europe.geo.json';

import arrowUp from '../../assets/img/arrow/arrow-up-small-white.svg';
import arrowDown from '../../assets/img/arrow/arrow-down-small-white.svg';
import arrowNeutral from '../../assets/img/arrow/arrow-neutral-small-white.svg';

import COLORS from '../../core/constants/colors';
import { PriceMapModel } from '../../core/models/gas-map';

highchartsMap(Highcharts);

interface IPriceMapChart {
  data: PriceMapModel[];
}

function reflowCharts() {
  if (Highcharts.charts && Highcharts.charts.length) {
    Highcharts.charts.forEach((c) => {
      if (c) {
        c.reflow();
      }
    });
  }
}

/* eslint-disable react/no-this-in-sfc */
const PriceMapChart = React.memo(function PriceMapChart({ data }: IPriceMapChart): JSX.Element {
  const chartData = data
    .filter((country) => country !== null)
    .map((country) => {
      return {
        code: country.code_Country,
        value: country.market_Value,
        name: country.market_Name,
        arrow: country.arrow,
      } as SeriesMapDataOptions;
    });

  const dataLabelsPosition: { [key: string]: string } = {
    GB: 'l',
    NL: 't',
    DE: 'l',
    IT: '',
    BE: 'l',
    AT: 'l',
  };

  const options: Highcharts.Options = {
    chart: {
      map: 'custom/europe',
      height: '100%',
      borderColor: undefined,
      animation: false,

      events: {
        load: function zoom() {
          this.mapZoom(0.55, 2300, 10000);
        },
      },
    },

    credits: {
      enabled: false,
    },

    title: {
      text: undefined,
    },

    subtitle: {
      text: undefined,
    },

    legend: {
      enabled: false,
    },

    plotOptions: {
      map: {
        borderColor: COLORS.white,
        nullColor: COLORS.nullContry,
        states: {
          hover: {
            color: COLORS.auxiliarContent,
          },
        },
        dragDrop: {
          draggableX: false,
          draggableY: false,
        },
      },
      series: {
        color: COLORS.lightLine,
        animation: false,
        dragDrop: {
          draggableX: false,
          draggableY: false,
        },
      },
    },

    mapNavigation: {
      enabled: false,
      enableButtons: false,
      enableDoubleClickZoom: false,
      enableMouseWheelZoom: false,
      enableTouchZoom: false,
      enableDoubleClickZoomTo: false,
    },

    series: [
      {
        name: 'Country',
        type: 'map',
        joinBy: ['iso-a2', 'code'],

        mapData,
        data: [...chartData],
        dataLabels: {
          enabled: true,
          useHTML: true,
          format: undefined,
          formatter: function formatter() {
            if (this.point.value) {
              // eslint-disable-next-line prefer-destructuring
              const point: any = this.point;
              const pointArrow = point.arrow;

              let type: string;
              let arrow: string;

              if (pointArrow > 0) {
                type = 'green';
                arrow = arrowUp;
              } else if (pointArrow === 0) {
                type = 'blue';
                arrow = arrowNeutral;
              } else {
                type = 'red';
                arrow = arrowDown;
              }

              return `
              <div class="formatterPrice">
                <div class="formatterPrice__border ${type} fcc">
                  <div class="formatterPrice__circle ${type}">
                    <div class="formatterPrice__card ${type} ${
                dataLabelsPosition[(this.point as any).code] || ''
              }">
                      <p>${this.point.options.name}</p>
                      <p>${this.point.value.toFixed(2)} <img src="${arrow}" /></p>
                    </div>
                  </div>
                </div>
              </div>
              `;
            }

            return '';
          },
        },
        tooltip: {
          headerFormat: '',
          pointFormat: '{point.name}',
        },
      },
    ],
  };

  useEffect(() => {
    setTimeout(() => {
      reflowCharts();
    }, 1000);
  }, []);

  return (
    <div className="MapChart">
      <div className="MapChart__wrapper">
        <HighchartsReact constructorType="mapChart" highcharts={Highcharts} options={options} />
      </div>
    </div>
  );
});

export default PriceMapChart;
